// ============================================================
//  useCart — minimal localStorage-backed cart for Offre blocks
//
//  Single source of truth: localStorage key `em_cart_<eventKey>`
//  - eventKey is derived from URL: published page uses slug,
//    editor preview falls back to "preview".
//  - Items are deduped by offerId.
//  - Cross-component sync via window 'storage' + custom event.
// ============================================================

import { useCallback, useEffect, useState } from 'react';

export interface CartItem {
  offerId: string;
  title: string;
  price: number;
  quantity: number;
  description?: string;
  inclusions?: string[];
}

const STORAGE_PREFIX = 'em_cart_';
const CHANGE_EVENT = 'em-cart-change';

function getEventKey(): string {
  // /{company}/published/{slug}  → slug
  // /{company}/events/{id}/...   → "preview-<id>"
  // anything else                → "default"
  const parts = window.location.pathname.split('/').filter(Boolean);
  const pubIdx = parts.indexOf('published');
  if (pubIdx >= 0 && parts[pubIdx + 1]) {
    return parts[pubIdx + 1];
  }
  const evtIdx = parts.indexOf('events');
  if (evtIdx >= 0 && parts[evtIdx + 1]) {
    return `preview-${parts[evtIdx + 1]}`;
  }
  return 'default';
}

function readStorage(key: string): CartItem[] {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as CartItem[]) : [];
  } catch {
    return [];
  }
}

function writeStorage(key: string, items: CartItem[]): void {
  localStorage.setItem(key, JSON.stringify(items));
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { key } }));
}

interface UseCartReturn {
  items: CartItem[];
  total: number;
  count: number;
  hasItem: (offerId: string) => boolean;
  addItem: (item: CartItem) => void;
  removeItem: (offerId: string) => void;
  toggleItem: (item: CartItem) => void;
  updateQuantity: (offerId: string, quantity: number) => void;
  clear: () => void;
}

export function useCart(): UseCartReturn {
  const storageKey = STORAGE_PREFIX + getEventKey();
  const [items, setItems] = useState<CartItem[]>(() => readStorage(storageKey));

  // Sync from storage / cross-component changes
  useEffect(() => {
    const refresh = (): void => setItems(readStorage(storageKey));
    const onStorage = (e: StorageEvent): void => {
      if (e.key === storageKey) refresh();
    };
    const onChange = (e: Event): void => {
      const detail = (e as CustomEvent).detail as { key?: string } | undefined;
      if (!detail?.key || detail.key === storageKey) refresh();
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener(CHANGE_EVENT, onChange);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener(CHANGE_EVENT, onChange);
    };
  }, [storageKey]);

  const hasItem = useCallback(
    (offerId: string) => items.some((i) => i.offerId === offerId),
    [items]
  );

  const addItem = useCallback(
    (item: CartItem) => {
      const current = readStorage(storageKey);
      if (current.some((i) => i.offerId === item.offerId)) return;
      const next = [...current, { ...item, quantity: item.quantity || 1 }];
      writeStorage(storageKey, next);
    },
    [storageKey]
  );

  const removeItem = useCallback(
    (offerId: string) => {
      const current = readStorage(storageKey);
      const next = current.filter((i) => i.offerId !== offerId);
      writeStorage(storageKey, next);
    },
    [storageKey]
  );

  const toggleItem = useCallback(
    (item: CartItem) => {
      const current = readStorage(storageKey);
      const exists = current.some((i) => i.offerId === item.offerId);
      const next = exists
        ? current.filter((i) => i.offerId !== item.offerId)
        : [...current, { ...item, quantity: item.quantity || 1 }];
      writeStorage(storageKey, next);
    },
    [storageKey]
  );

  const updateQuantity = useCallback(
    (offerId: string, quantity: number) => {
      const current = readStorage(storageKey);
      const qty = Math.max(1, Math.floor(quantity));
      const next = current.map((i) =>
        i.offerId === offerId ? { ...i, quantity: qty } : i
      );
      writeStorage(storageKey, next);
    },
    [storageKey]
  );

  const clear = useCallback(() => {
    writeStorage(storageKey, []);
  }, [storageKey]);

  const total = items.reduce(
    (sum, i) => sum + (Number(i.price) || 0) * (i.quantity || 1),
    0
  );
  const count = items.length;

  return {
    items,
    total,
    count,
    hasItem,
    addItem,
    removeItem,
    toggleItem,
    updateQuantity,
    clear,
  };
}
